import { getInventoryByLocation } from "@utils/api"
import React from "react"

export const INVENTORY_ITEM_TRACKED = "INVENTORY_ITEM_TRACKED"
const INVENTORY_POLICY_CONTINUE = "CONTINUE"

export interface InventoryData {
  [sku: string]: {
    [locationId: string]: {
      [INVENTORY_ITEM_TRACKED]: boolean
      isAvailable: boolean
      quantity: number
    }
  }
}

export const formatInventoryData = (
  responseData,
  includeCollectionsForSkus = [] as string[]
): InventoryData => {
  const inventoryData: InventoryData = {}
  const cleanedData = (responseData || []).filter(Boolean)

  if (cleanedData.length) {
    cleanedData.forEach(
      ({ collections, inventory, inventoryPolicy, sku, tracked }) => {
        const locationData: Record<string, any> = {}
        const safeInventory = inventory || []

        safeInventory.forEach(({ available, locationId }) => {
          locationData[locationId] = {
            isAvailable:
              available > 0 || inventoryPolicy === INVENTORY_POLICY_CONTINUE,
            quantity: available,
          }
        })

        if (
          includeCollectionsForSkus?.includes(sku) &&
          Array.isArray(collections)
        ) {
          locationData.collections = collections.filter(Boolean)
        }

        const cleanedSku = String(sku).replace("__", "/")
        inventoryData[cleanedSku] = locationData
        inventoryData[cleanedSku][INVENTORY_ITEM_TRACKED] = tracked || false
      }
    )
  }

  return inventoryData
}

export default function useMultiWarehouseInventory(
  skus: string[] | undefined,
  includeCollectionsForSkus = [] as string[]
) {
  const [inventoryData, setInventoryData] = React.useState<InventoryData>({})

  const skusKey = React.useMemo(() => {
    if (!Array.isArray(skus) || !skus.length) return ""
    return [...new Set(skus)].sort().join("|")
  }, [Array.isArray(skus) ? skus.join("|") : ""])

  // Prevent duplicate calls for the same key, including StrictMode double run
  const inFlightKey = React.useRef<null | string>(null)
  const mounted = React.useRef(true)

  React.useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  React.useEffect(() => {
    if (!skusKey) return
    if (inFlightKey.current === skusKey) return // de-dupe

    inFlightKey.current = skusKey
    let cancelled = false

    getInventoryByLocation(skus!)
      .then((data) => {
        if (cancelled || !mounted.current) return
        if (data)
          setInventoryData(formatInventoryData(data, includeCollectionsForSkus))
      })
      .finally(() => {
        if (inFlightKey.current === skusKey) inFlightKey.current = null
      })

    return () => {
      cancelled = true
    }
  }, [skusKey])

  return { inventoryData }
}
